/**
 * Helpers para dump completo de base de datos (Sistema → Datos).
 *
 * El backend exporta/importa de forma dinámica (information_schema).
 * KNOWN_PUBLIC_TABLES documenta el esquema esperado tras migraciones 001–012.
 */

export const DUMP_FORMAT = 'soquimat-db-dump';
export const DUMP_VERSION = 2;

/** Tablas públicas esperadas en dump (alineado con backend/api/data_transfer_base.py). */
export const KNOWN_PUBLIC_TABLES = [
  'users',
  'organizations',
  'organization_units',
  'contacts',
  'contact_activities',
  'paint_products',
  'paint_colors',
  'paint_product_formats',
  'paint_product_costs',
  'paint_format_categories',
  'upselling_categories',
  'upselling_formats',
  'upselling_products',
  'upselling_brands',
  'quotations',
  'quotation_items',
  'sales',
  'licitations',
  'deliveries',
  'delivery_trips',
  'finance_accounts',
  'finance_entries',
  'finance_account_levels',
  'finance_chart_accounts',
  'finance_hierarchy_nodes',
  'finance_detail_nodes',
  'prospecting_rubric_criteria',
  'prospecting_rubric_profiles',
  'prospecting_rubric_profile_weights',
  'organization_rubrics',
  'organization_rubric_scores',
];

export const downloadTextFile = (filename, content, mimeType = 'text/plain;charset=utf-8') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const isValidDump = (payload) => {
  if (!payload || typeof payload !== 'object') return false;
  if (payload.format !== DUMP_FORMAT) return false;
  if (!payload.tables || typeof payload.tables !== 'object') return false;
  return true;
};

export const summarizeDump = (payload) => {
  if (!isValidDump(payload)) {
    return { tableCount: 0, totalRows: 0, tables: [], schemaCoverage: null };
  }
  const tables = Object.keys(payload.tables)
    .sort()
    .map((name) => {
      const rows = Array.isArray(payload.tables[name]) ? payload.tables[name].length : 0;
      return { name, rows };
    });

  const dumpTableSet = new Set(Object.keys(payload.tables));
  const missingKnownTables = KNOWN_PUBLIC_TABLES.filter((name) => !dumpTableSet.has(name));

  return {
    tableCount: tables.length,
    totalRows: tables.reduce((sum, item) => sum + item.rows, 0),
    tables,
    exportedAt: payload.exported_at || null,
    version: payload.version || null,
    schemaCoverage: payload.schema_coverage || null,
    missingKnownTables,
  };
};

/**
 * Une dumps parciales (p. ej. export tabla a tabla detrás de CloudFront)
 * en un único dump importable.
 */
export const mergeDumps = (dumps = [], { exportScope = 'full' } = {}) => {
  const list = (Array.isArray(dumps) ? dumps : []).filter(isValidDump);
  if (!list.length) {
    throw new Error('No hay dumps parciales para unir.');
  }

  const tables = {};
  const rowCounts = {};
  const schemaColumns = {};
  const tableOrder = [];
  const requested = [];

  list.forEach((dump) => {
    Object.entries(dump.tables || {}).forEach(([name, rows]) => {
      tables[name] = rows;
      rowCounts[name] = Array.isArray(rows) ? rows.length : 0;
    });
    Object.assign(schemaColumns, dump.schema_columns || {});
    (dump.table_order || Object.keys(dump.tables || {})).forEach((name) => {
      if (!tableOrder.includes(name)) tableOrder.push(name);
    });
    (dump.requested_tables || []).forEach((name) => {
      if (!requested.includes(name)) requested.push(name);
    });
  });

  const first = list[0];
  const last = list[list.length - 1];

  return {
    format: DUMP_FORMAT,
    version: last.version || first.version || DUMP_VERSION,
    exported_at: new Date().toISOString().replace(/\.\d{3}Z$/, 'Z'),
    export_scope: exportScope,
    requested_tables: requested.length ? requested : tableOrder,
    table_order: tableOrder,
    row_counts: rowCounts,
    schema_columns: schemaColumns,
    schema_coverage: last.schema_coverage || first.schema_coverage || null,
    date_health: {
      invalid_count: 0,
      suspicious_count: 0,
    },
    tables,
    merged_from_batches: list.length,
  };
};

/**
 * Ordena filas con FK a la misma tabla (padre antes que hijo). Al importar por tramos
 * el backend solo ordena dentro de cada tramo, así que el orden global se fija aquí.
 */
export const orderRowsForSelfFk = (rows = [], selfFkColumns = []) => {
  if (!selfFkColumns.length || rows.length < 2) return rows;
  const withId = rows.filter((row) => row?.id !== null && row?.id !== undefined);
  const withoutId = rows.filter((row) => row?.id === null || row?.id === undefined);
  const remaining = new Map(withId.map((row) => [String(row.id), row]));
  const ordered = [];

  while (remaining.size) {
    const ready = [];
    remaining.forEach((row, key) => {
      const blocked = selfFkColumns.some((col) => {
        const parent = row[col];
        return parent !== null && parent !== undefined && parent !== '' && remaining.has(String(parent)) && String(parent) !== key;
      });
      if (!blocked) ready.push(key);
    });
    if (!ready.length) {
      // Ciclo o referencias colgantes: se envían tal cual y la BD reportará el problema.
      remaining.forEach((row) => ordered.push(row));
      break;
    }
    ready.forEach((key) => {
      ordered.push(remaining.get(key));
      remaining.delete(key);
    });
  }
  return ordered.concat(withoutId);
};

/** Divide filas en tramos acotados por cantidad y por tamaño JSON aproximado (bytes). */
export const buildRowChunks = (rows = [], { maxRows = 1000, maxBytes = 1000000 } = {}) => {
  const chunks = [];
  let current = [];
  let currentBytes = 0;
  let offset = 0;

  rows.forEach((row) => {
    const rowBytes = JSON.stringify(row).length + 1;
    if (current.length && (current.length >= maxRows || currentBytes + rowBytes > maxBytes)) {
      chunks.push({ offset, rows: current });
      offset += current.length;
      current = [];
      currentBytes = 0;
    }
    current.push(row);
    currentBytes += rowBytes;
  });
  if (current.length) chunks.push({ offset, rows: current });
  return chunks;
};

const TRANSIENT_HTTP_STATUSES = new Set([502, 503, 504]);

/** Cortes de red / proxy que tiene sentido reintentar (no errores de datos del backend). */
export const isTransientError = (err) =>
  err?.code === 'TIMEOUT' ||
  err?.code === 'NETWORK' ||
  (err?.code === 'HTML_INSTEAD_OF_JSON' && TRANSIENT_HTTP_STATUSES.has(err?.status));

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Ejecuta `fn` reintentando solo errores transitorios. Si se agotan los intentos,
 * el mensaje del error indica cuántos se hicieron.
 */
export const withRetries = async (fn, { attempts = 3, delaysMs = [3000, 8000], onRetry } = {}) => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await fn(attempt);
    } catch (err) {
      const transient = isTransientError(err);
      if (!transient || attempt >= attempts) {
        if (attempt > 1 && err instanceof Error) {
          err.message = transient
            ? `${err.message} (falló en los ${attempt} intentos)`
            : `${err.message} (en el intento ${attempt}; los anteriores fueron cortes de conexión o del proxy)`;
        }
        throw err;
      }
      const delay = delaysMs[Math.min(attempt - 1, delaysMs.length - 1)] || 0;
      if (onRetry) onRetry(attempt, err, delay);
      await wait(delay);
    }
  }
};

export const parseDumpFile = async (file) => {
  const text = await file.text();
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('El archivo no es un JSON válido.');
  }
  if (!isValidDump(parsed)) {
    throw new Error(
      `Formato inválido. Se espera un dump con format="${DUMP_FORMAT}" y un objeto "tables".`
    );
  }
  return parsed;
};
